'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { CallToAction } from '@/components/tour/CallToAction';
import { createPortal } from 'react-dom';
import { TourReservationModal } from './TourReservationModal';
import { useSearchParams } from 'next/navigation';
import { fetchAvailability } from '@/actions/fetch-availability';
import { TourOption } from './TourOption';
import { CallToActionSelected } from '../tour/CallToActionSelected';
import FloatingButton from '../buttons/FloatingButton';

interface ITourReservationProps {
  tourId: string;
  yearMonth: {
    [key: string]: Array<number>;
  };
}

export default function TourReservation({ tourId, yearMonth }: ITourReservationProps) {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [tourSchedule, setTourSchedule] = useState<Array<string>>([]);
  const params = useSearchParams();

  const tourParams = useMemo(() => {
    const year = params.get('year');
    const month = params.get('month');
    const day = params.get('day');
    const adults = params.get('adults');
    const children = params.get('children');

    if (year == null) return null;
    if (month == null) return null;
    if (day == null) return null;
    if (adults == null) return null;
    if (children == null) return null;

    return {
      year: Number(year),
      month: Number(month),
      day: Number(day),
      adults: Number(adults),
      children: Number(children),
    };
  }, [params]);

  const handleOpen = () => setIsModalOpen(true);
  const handleClose = () => setIsModalOpen(false);

  useEffect(() => {
    if (!tourParams) return;

    const { year, month, day, adults, children } = tourParams;
    fetchAvailability({ tourId, year, month, adults, children })
      .then((data) => {
        const targetDay = data.find((date) => date.day === day);
        if (!targetDay) return setTourSchedule([]);
        setTourSchedule(targetDay.time);
      })
      .catch(console.error);
  }, [params]);

  return (
    <>
      <div className="h-full hidden lg:block relative">
        <div className="tour-cta__content flex flex-col gap-1">
          {tourParams ? (
            <CallToActionSelected handleClick={handleOpen} tourParams={tourParams} />
          ) : (
            <CallToAction handleClick={handleOpen} />
          )}
          {tourSchedule.map((startTime) => (
            <TourOption key={startTime} startTime={startTime} />
          ))}
        </div>
      </div>
      {createPortal(
        <TourReservationModal isOpen={isModalOpen} tourId={tourId} yearMonth={yearMonth} handleClose={handleClose} />,
        document.body
      )}
      {/* Mobile floating button */}
      {!isModalOpen && (
        <div className="fixed w-0 flex lg:hidden justify-center align-top left-1/2 bottom-2 z-[1000]">
          <FloatingButton handleClick={handleOpen} />
        </div>
      )}
    </>
  );
}
